import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { loadAnalytics, revokeAnalytics, trackPageView } from '@/lib/analytics/load'
import { defaultConsent, readConsent, writeConsent, type Consent } from '@/lib/analytics/consent'
import { ConsentBanner } from '@/components/analytics/ConsentBanner'

/**
 * Loads third-party tags once consent allows, and reports SPA navigations.
 *
 * The captured site fired a page view on every full page load. Client-side
 * routing removes those loads, so navigations have to be reported explicitly
 * or analytics silently under-counts every page after the first. The landing
 * page is reported too: GA4 runs with `send_page_view: false`, so skipping it
 * here meant no visit was ever counted on the page it arrived on.
 *
 * Outside Europe, a visitor who hasn't chosen gets `defaultConsent()`: tags run
 * and the banner is a notice they can decline. In Europe nothing runs until
 * they accept.
 */
export function Analytics() {
  const { pathname } = useLocation()
  const [consent, setConsent] = useState<Consent | null>(null)
  const [decided, setDecided] = useState(true)
  const [notice, setNotice] = useState(false)
  const firstView = useRef(true)

  useEffect(() => {
    const stored = readConsent()
    const effective = stored ?? defaultConsent()
    setConsent(effective)
    setDecided(stored !== null)
    setNotice(stored === null && effective !== null)
    if (effective) loadAnalytics(effective)
  }, [])

  useEffect(() => {
    if (!consent) return
    if (firstView.current) {
      firstView.current = false
      trackPageView(pathname, document.title, { pixel: false })
      return
    }
    trackPageView(pathname, document.title)
  }, [pathname, consent])

  const decide = (choice: { analytics: boolean; marketing: boolean }) => {
    const stored = writeConsent(choice)
    setConsent(stored)
    setDecided(true)
    loadAnalytics(stored)
    revokeAnalytics(stored)
  }

  if (decided) return null
  return <ConsentBanner onDecide={decide} mode={notice ? 'notice' : 'ask'} />
}
